import { DISPLAY_TIME_ZONE, STALE_AFTER_MS, VERY_STALE_AFTER_MS } from './constants.js';

/**
 * UK display rules, shared by the dashboard (and mirrored by the agents):
 * miles (yards below 0.1 mi), mph, DD/MM/YYYY and 24-hour times in Europe/London.
 */

export const METRES_PER_MILE = 1609.344;
export const METRES_PER_YARD = 0.9144;
const MPS_TO_MPH = 3600 / METRES_PER_MILE;

/** "150 yd" below 0.1 mi, "2.4 mi" below 10 mi, "12 mi" above. */
export function formatDistance(metres: number): string {
  const miles = metres / METRES_PER_MILE;
  if (miles < 0.1) return `${Math.round(metres / METRES_PER_YARD)} yd`;
  if (miles < 10) return `${miles.toFixed(1)} mi`;
  return `${Math.round(miles).toLocaleString('en-GB')} mi`;
}

/** Location accuracy radius, e.g. "±20 yd" or "±16 mi" for an IP-based fix. */
export function formatAccuracy(metres: number): string {
  return `±${formatDistance(metres)}`;
}

export function formatSpeed(metresPerSecond: number): string {
  return `${Math.round(metresPerSecond * MPS_TO_MPH)} mph`;
}

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: DISPLAY_TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const timeFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: DISPLAY_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** "23/10/2026" */
export function formatDate(date: Date | string | number): string {
  return dateFormatter.format(new Date(date));
}

/** "21:05" */
export function formatTime(date: Date | string | number): string {
  return timeFormatter.format(new Date(date));
}

/** "23/10/2026 21:05" */
export function formatDateTime(date: Date | string | number): string {
  return `${formatDate(date)} ${formatTime(date)}`;
}

/** "just now", "3 min ago", "2 hours ago", "yesterday", "4 days ago", then the date. */
export function formatRelative(date: Date | string | number, now: Date | number = Date.now()): string {
  const diffMs = new Date(now).getTime() - new Date(date).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return formatDate(date);
}

export interface LatLon {
  lat: number;
  lon: number;
}

/** Great-circle distance in metres. */
export function haversineMetres(a: LatLon, b: LatLon): number {
  const R = 6_371_008.8;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Steps shorter than this are treated as GPS jitter, however accurate the fixes claim to be. */
const MIN_STEP_M = 10;

/**
 * Distance travelled along a route, in metres. A device sitting still still reports
 * fixes that wander by about their accuracy, so a step only counts once the device has
 * moved further than the accuracy of the two fixes; the wobble in between is ignored
 * rather than adding up to phantom miles overnight.
 */
export function routeDistanceMetres(points: readonly (LatLon & { accuracyM: number })[]): number {
  let total = 0;
  let anchor = points[0];
  if (!anchor) return 0;
  for (const p of points.slice(1)) {
    const step = haversineMetres(anchor, p);
    if (step > Math.max(anchor.accuracyM, p.accuracyM, MIN_STEP_M)) {
      total += step;
      anchor = p;
    }
  }
  return total;
}

export type Staleness = 'fresh' | 'stale' | 'very_stale';

/** Grey after 1 hour without contact, red after 24 hours. */
export function staleness(
  lastSeen: Date | string | number | null,
  now: Date | number = Date.now(),
): Staleness {
  if (lastSeen === null) return 'very_stale';
  const age = new Date(now).getTime() - new Date(lastSeen).getTime();
  if (age > VERY_STALE_AFTER_MS) return 'very_stale';
  if (age > STALE_AFTER_MS) return 'stale';
  return 'fresh';
}
